//Libraries
import { get } from "lodash";
import { styled } from "@mui/system";
import { Grid } from "@mui/material";
import { useState, useEffect } from "react";

//Typing
import { Draft } from "src/types/common";
import { IContact } from "src/types/org/di/diInterface";
import { IFieldErrors } from "src/types/validation";

//Processing
import { ChangeManager } from "src/generic/utils/form";
import { standardize } from "src/generic/type/modify";

//Components
import { Typography } from "src/components";
import { Input } from "src/generic/component/form";
import { FieldNote } from "src/generic/component";

const FieldRow = styled(Grid)`
  margin-bottom: 10px;
`;

interface ContactFieldProps {
  readOnly?: boolean;
  errors: IFieldErrors;
  contact?: Draft<IContact>;
  original?: Draft<IContact>;
  showDiff?: boolean;
  onUpdate?: (contact: Draft<IContact>) => void;
}

export const ContactFields = ({ readOnly, errors, contact, original, showDiff, onUpdate }: ContactFieldProps) => {
  const { name, title, phone, email } = contact || {};

  useEffect(() => {
    /* TODO: Need useEffect logic for contact */
  }, [contact]);

  // if (showDiff && !original) throw new Error("Must send the original value when comparing differences.");

  const { getCurrent, isChanged, getChangeColor } = ChangeManager<IContact>(original || {}, contact || {});

  const changed = (updates: Draft<IContact>) => {
    if (onUpdate) {
      onUpdate(standardize({ ...contact, ...updates }));
    }
  };

  const getErrors = (field: string): string[] => {
    //return get(errors, `${field}._errors`, []);
    const error = get(errors, `${field}._errors`, []);
    return Array.isArray(error) ? error : [];
  };

  const hasErrors = (field: string): boolean => {
    return getErrors(field).length > 0 ? true : false;
  };

  return (
    <>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <Typography variant="body1">Contact Name:</Typography>

            {showDiff && isChanged("name") && (
              <div>
                <i className={getChangeColor("name")}>Current: {getCurrent("name") || "(empty)"}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor("name") : ""}
              validation={hasErrors("name") ? "error" : undefined}
              validationMsg={getErrors("name").join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ name: newVal });
              }}
              value={name || ""}
            />
          </FieldRow>
          <FieldRow>
            <Typography variant="body1">Title:</Typography>
            {showDiff && isChanged("title") && (
              <div>
                <i className={getChangeColor("title")}>Current: {getCurrent("title") || "(empty)"}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor("title") : ""}
              validation={hasErrors("title") ? "error" : undefined}
              validationMsg={getErrors("title").join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ title: newVal });
              }}
              value={title || ""}
            />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <Typography variant="body1">Phone Number:</Typography>
            {showDiff && isChanged("phone") && (
              <div>
                <i className={getChangeColor("phone")}>Current: {getCurrent("phone") || "(empty)"}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor("phone") : ""}
              validation={hasErrors("phone") ? "error" : undefined}
              validationMsg={getErrors("phone").join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ phone: newVal });
              }}
              value={phone || ""}
            />
          </FieldRow>

          <FieldRow>
            <Typography variant="body1">Email:</Typography>
            {showDiff && isChanged("email") && (
              <div>
                <i className={getChangeColor("email")}>Current: {getCurrent("email") || "(empty)"}</i>
              </div>
            )}
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor("email") : ""}
              validation={hasErrors("email") ? "error" : undefined}
              validationMsg={getErrors("email").join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ email: newVal });
              }}
              value={email || ""}
            />
          </FieldRow>
        </Grid>
      </Grid>
    </>
  );
};

export const formatContact = (contact: Draft<IContact>) => {
  return <>
    <div>{contact.name}</div>
    <div>{contact.title}</div>
    <div>{contact.phone}</div>
    <div>{contact.email}</div>
    <div></div>
  </>;
};
